import type { Chip, ChipState, ChipType } from "../../core/chip";
import type { Route } from "../../core/route";
import type {
  GameSession,
  ModuleManifest,
  ModuleScene,
} from "../contracts/types";
import { visibleActions } from "../runtime/sessionRuntime";

const NOW = new Date("2026-07-23T12:00:00.000Z").toISOString();

function sceneChipType(scene: ModuleScene): ChipType {
  if (scene.type === "ENDING") return "ALERT";
  if (scene.type === "CONFLICT" || scene.type === "TERMINAL") return "ALERT";
  if (scene.type === "PASSAGE") return "DOCUMENT";
  return "PLACE";
}

function mapVisualState(
  scene: ModuleScene,
  session: GameSession,
): ChipState {
  if (scene.id === session.currentSceneId) return "ACTIVE";
  if (scene.type === "ENDING" && session.discovered.includes(scene.id))
    return "ENDING";
  if (scene.state === "DANGER" || scene.type === "CONFLICT") return "DANGER";
  if (scene.state === "INTRUSION" || scene.type === "TERMINAL")
    return "INTRUSION";
  if (session.discovered.includes(scene.id)) return "AVAILABLE";
  return "PENDING";
}

/**
 * Project only the discovered neighborhood into CHIPMAP chips.
 * Hidden story graph remains unseen until discovered.
 */
export function projectSessionToChips(
  module: ModuleManifest,
  session: GameSession,
): Chip[] {
  const discovered = new Set(session.discovered);
  const scenes = module.scenes.filter((s) => discovered.has(s.id));

  return scenes.map((scene) => {
    const actions = visibleActions(
      module,
      // Routes for non-current scenes: show topology only
      scene.id === session.currentSceneId
        ? session
        : { ...session, currentSceneId: scene.id },
    );

    const routes: Route[] = [];
    // Authored topology edges among discovered chips
    for (const [from, to] of module.routes) {
      if (from !== scene.id) continue;
      if (!discovered.has(to)) continue;
      const target = module.scenes.find((s) => s.id === to);
      routes.push({
        id: `${from}->${to}`,
        kind: "DESTINATION",
        label: target?.title ?? to,
        targetChipId: to,
        state: "AVAILABLE",
      });
    }
    // Legal actions as procedure routes when on current scene
    if (scene.id === session.currentSceneId) {
      for (const action of actions) {
        if (action.target && discovered.has(action.target)) {
          routes.push({
            id: `act:${action.id}`,
            kind: "PROCEDURE",
            label: action.label,
            targetChipId: action.target,
            state: "RECOMMENDED",
          });
        } else if (!action.target) {
          routes.push({
            id: `act:${action.id}`,
            kind: "PROCEDURE",
            label: action.label,
            state: "AVAILABLE",
          });
        }
      }
    }

    return {
      id: scene.id,
      title: scene.title,
      type: sceneChipType(scene),
      state: mapVisualState(scene, session),
      summary: scene.body.join(" "),
      goal: scene.subtitle,
      known: [
        ...(scene.objects ?? []),
        ...(scene.present ?? []),
        `TYPE:${scene.type}`,
        "DEVELOPMENT FIXTURE / NON-AUTHORITATIVE",
      ],
      open: actions.map((a) => a.label),
      imprints: [],
      routes,
      truth: "NONE",
      risk:
        scene.type === "CONFLICT" || scene.type === "TERMINAL"
          ? "REVIEW"
          : "CLEAR",
      sourceRefs: [
        `module:${module.id}`,
        `fixture:${module.classification}`,
      ],
      revision: session.saveRevision,
      createdAt: NOW,
      updatedAt: session.updatedAt,
    };
  });
}

export type LayoutPosition = { x: number; y: number };

/** Convert module percent layout (0–100) into CHIPMAP viewBox coordinates. */
export function projectLayout(
  module: ModuleManifest,
  session: GameSession,
  viewW = 720,
  viewH = 1000,
  pad = 48,
): Record<string, LayoutPosition> {
  const positions: Record<string, LayoutPosition> = {};
  for (const scene of module.scenes) {
    if (!session.discovered.includes(scene.id)) continue;
    positions[scene.id] = {
      x: pad + (scene.x / 100) * (viewW - pad * 2),
      y: pad + (scene.y / 100) * (viewH - pad * 2),
    };
  }
  return positions;
}

export function getScene(
  module: ModuleManifest,
  sceneId: string,
): ModuleScene | undefined {
  return module.scenes.find((s) => s.id === sceneId);
}

export function projectManifestRows(session: GameSession, moduleTitle: string) {
  return [
    ["AUTHORITY", "GAME_SESSION", "gold"],
    ["MODULE", moduleTitle, "cyan"],
    ["MODULE ID", session.moduleId, ""],
    ["SAVE REV", String(session.saveRevision), ""],
    ["SCENE", session.currentSceneId, "cyan"],
    ["ENDING", session.ending ?? "NONE", session.ending ? "gold" : ""],
    ["BODY", String(session.attributes.body ?? 0), ""],
    ["MIND", String(session.attributes.mind ?? 0), ""],
    ["SOUL", String(session.attributes.soul ?? 0), ""],
    ["INTEGRITY", String(session.resources.integrity ?? 0), "green"],
    ["STRESS", String(session.resources.stress ?? 0), ""],
    ["SIGNAL", String(session.resources.signal ?? 0), ""],
    ["INVENTORY", session.inventory.join(", ") || "—", ""],
    ["FLAGS", session.flags.join(", ") || "—", "gold"],
    ["LORE UNLOCKS", String(session.lore.length), "cyan"],
    ["DISCOVERED", String(session.discovered.length), ""],
    ["CLASSIFICATION", "DEVELOPMENT FIXTURE / NON-AUTHORITATIVE", "muted"],
  ] as [string, string, string][];
}
